import React, { useEffect, useState, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  BanknotesIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  CalendarIcon,
  CameraIcon,
  DocumentDuplicateIcon,
  DocumentMagnifyingGlassIcon,
  XMarkIcon,
  ExclamationTriangleIcon,
  Cog6ToothIcon,
  BriefcaseIcon,
  ArrowDownTrayIcon,
  MapPinIcon,
  ClockIcon,
  ScissorsIcon,
  PrinterIcon,
  DocumentTextIcon,
} from '@heroicons/react/24/outline';
import { useExpenseStore } from '../store/expenseStore';
import { useExpenseCategoryStore } from '../store/expenseCategoryStore';
import { useProjectStore } from '../store/projectStore';
import { useClientStore } from '../store/clientStore';
import { useTripStore } from '../store/tripStore';
import { Modal } from '../components/ui/Modal';
import { Input } from '../components/ui/Input';
import { MileageSection } from '../components/expenses/MileageSection';
import { PerDiemSection } from '../components/expenses/PerDiemSection';
import { ExpensePolicyManager } from '../components/expenses/ExpensePolicyManager';
import { Button } from '../components/ui/Button';
import {
  Expense,
  ExpenseCreate,
  ExpenseUpdate,
  BusinessTrip,
  TripCreate,
  TripUpdate,
  ProjectSubproject,
  receiptsAPI,
  tripsAPI,
  subprojectsAPI,
  expensePoliciesAPI,
  expensesAPI,
  PolicyCheckResult,
  getReceiptUrl,
} from '../services/api';

const getLocalDateString = (date: Date = new Date()): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const PAYMENT_METHOD_LABELS: Record<string, string> = {
  credit_card: 'Credit Card',
  debit_card: 'Debit Card',
  cash: 'Cash',
  check: 'Check',
  bank_transfer: 'Bank Transfer',
  company_card: 'Company Card',
  personal_card: 'Personal Card',
  other: 'Other',
};

export const ExpensesPage: React.FC = () => {
  const {
    expenses,
    fetchExpenses,
    createExpense,
    updateExpense,
    deleteExpense,
    isLoading,
    error,
    clearError,
  } = useExpenseStore();
  const { categories: expenseCategories, fetchCategories: fetchExpenseCategories } = useExpenseCategoryStore();
  const { projects, fetchProjects } = useProjectStore();
  const { clients, fetchClients } = useClientStore();
  const { trips, fetchTrips, createTrip, updateTrip, deleteTrip } = useTripStore();

  // Page is trip-only now (all expenses are tied to a trip). Kept as a typed
  // constant so the (inert) legacy expenses/mileage/per-diem blocks still compile.
  const [activeTab] = useState<'expenses' | 'mileage' | 'per-diem' | 'trips'>('trips');
  // Legacy expenses-view flag (page is trip-only now; retained for the inert blocks)
  const [expenseView] = useState<'day' | 'all'>('all');

  // ─── Trip state ────────────────────────────────────────────────────────
  const [selectedTrip, setSelectedTrip] = useState<BusinessTrip | null>(null);
  const [tripExpenses, setTripExpenses] = useState<Expense[]>([]);
  const [isTripModalOpen, setIsTripModalOpen] = useState(false);
  const [editingTrip, setEditingTrip] = useState<BusinessTrip | null>(null);
  const [isDeletingTrip, setIsDeletingTrip] = useState(false);
  const [tripToDelete, setTripToDelete] = useState<BusinessTrip | null>(null);
  const [isTripSubmitting, setIsTripSubmitting] = useState(false);
  const [tripFormError, setTripFormError] = useState('');
  const [tripFormData, setTripFormData] = useState<{
    project_id: string; subproject_id: string; name: string; destination: string;
    start_date: string; end_date: string; purpose: string; status: string;
  }>({
    project_id: '', subproject_id: '', name: '', destination: '',
    start_date: '', end_date: '', purpose: '', status: 'active',
  });
  const [tripSubprojects, setTripSubprojects] = useState<ProjectSubproject[]>([]);
  const [isPolicyManagerOpen, setIsPolicyManagerOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState(getLocalDateString());
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [editingExpense, setEditingExpense] = useState<Expense | null>(null);
  const [isCopying, setIsCopying] = useState(false);
  const [deletingExpense, setDeletingExpense] = useState<Expense | null>(null);
  const [showCalendar, setShowCalendar] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formError, setFormError] = useState('');
  const [receiptPreview, setReceiptPreview] = useState<string | null>(null);
  const [isUploadingReceipt, setIsUploadingReceipt] = useState(false);
  const [isProcessingOCR, setIsProcessingOCR] = useState(false);
  const [policyWarnings, setPolicyWarnings] = useState<PolicyCheckResult | null>(null);
  const [pendingReceipt, setPendingReceipt] = useState<{ image_url: string; thumbnail_url: string } | null>(null);
  const [pendingOcrData, setPendingOcrData] = useState<any>(null);
  const [isOCRPrefilling, setIsOCRPrefilling] = useState(false);
  const [viewingReceipt, setViewingReceipt] = useState<{ imageUrl: string; ocr?: any; expenseId?: string; imagePath?: string } | null>(null);
  const [viewerOCRError, setViewerOCRError] = useState('');
  const [splitConfirm, setSplitConfirm] = useState(false);
  const [isSplitting, setIsSplitting] = useState(false);
  const [splitRows, setSplitRows] = useState<{ date: string; category_id: string; description: string; amount: string }[]>([]);
  // Multi-page receipt capture (snap several pages → one expense)
  const [isCaptureOpen, setIsCaptureOpen] = useState(false);
  const [capturePages, setCapturePages] = useState<{ image_url: string; thumbnail_url: string }[]>([]);
  const [captureTripId, setCaptureTripId] = useState<string | null>(null);
  const [isCaptureUploading, setIsCaptureUploading] = useState(false);
  const [isCombining, setIsCombining] = useState(false);
  const [captureError, setCaptureError] = useState('');
  const [snapMessage, setSnapMessage] = useState('');
  const [exportMenuOpen, setExportMenuOpen] = useState(false);
  // Label of the report currently being generated (e.g. "Word", "PDF"), or '' when idle.
  const [exportBusy, setExportBusy] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const captureInputRef = useRef<HTMLInputElement>(null);
  const location = useLocation();

  // Accept date from cross-page navigation (e.g. Search → Expenses)
  useEffect(() => {
    const stateDate = (location.state as { date?: string })?.date;
    if (stateDate) {
      setSelectedDate(stateDate);
      // Clear the state so refreshing doesn't re-apply
      window.history.replaceState({}, '');
    }
  }, [location.state]);

  const [formData, setFormData] = useState({
    project_id: '',
    expense_category_id: '',
    trip_id: '',
    date: selectedDate,
    payee: '',
    amount: '',
    description: '',
    notes: '',
    payment_method: 'credit_card',
    is_reimbursable: true,
  });

  useEffect(() => {
    fetchProjects();
    fetchClients();
    fetchExpenseCategories();
    fetchTrips();
  }, [fetchProjects, fetchClients, fetchExpenseCategories, fetchTrips]);

  useEffect(() => {
    localStorage.setItem('expenseView', expenseView);
  }, [expenseView]);

  useEffect(() => {
    if (snapMessage) {
      const t = setTimeout(() => setSnapMessage(''), 4000);
      return () => clearTimeout(t);
    }
  }, [snapMessage]);

  useEffect(() => {
    if (expenseView === 'all') {
      fetchExpenses({});
    } else {
      fetchExpenses({ date: selectedDate });
    }
  }, [expenseView, selectedDate, fetchExpenses]);

  // Refresh the expense list consistently with the current view.
  const refreshExpenses = () => {
    if (expenseView === 'all') {
      fetchExpenses({});
    } else {
      fetchExpenses({ date: selectedDate });
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString + 'T00:00:00');
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const formatShortDate = (dateString: string) => {
    const date = new Date(dateString + 'T00:00:00');
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const navigateDate = (direction: 'prev' | 'next') => {
    const current = new Date(selectedDate + 'T00:00:00');
    current.setDate(current.getDate() + (direction === 'prev' ? -1 : 1));
    setSelectedDate(getLocalDateString(current));
  };

  const goToToday = () => setSelectedDate(getLocalDateString());
  const isToday = selectedDate === getLocalDateString();

  const groupedProjects = clients.map(client => ({
    client,
    projects: projects.filter(p => p.client_id === client.id && p.is_active),
  })).filter(group => group.projects.length > 0);

  const activeProjects = projects.filter(p => p.is_active);
  const activeCategories = expenseCategories.filter(c => c.is_active);

  const dailyTotal = expenses.reduce((sum, e) => sum + e.amount, 0);

  // OCR-extracted receipt time (24h "HH:MM"), if present on the receipt.
  const receiptTime = (e: Expense): string => e.ocr_data?.extracted_fields?.time?.value || '';
  // Sort by date, then by receipt time when available (no-time entries sort last
  // within their date), then by creation order.
  const compareByDateTime = (a: Expense, b: Expense): number =>
    a.date.localeCompare(b.date)
    || (receiptTime(a) || '99:99').localeCompare(receiptTime(b) || '99:99')
    || (a.created_at || '').localeCompare(b.created_at || '');

  // "All" view: expenses across all dates, grouped by date (oldest first)
  const expensesOldestFirst = [...expenses].sort(compareByDateTime);
  const groupedByDate = expensesOldestFirst.reduce((acc, e) => {
    const g = acc.find(x => x.date === e.date);
    if (g) { g.items.push(e); g.subtotal += e.amount; }
    else acc.push({ date: e.date, items: [e], subtotal: e.amount });
    return acc;
  }, [] as { date: string; items: Expense[]; subtotal: number }[]);
  const allExpensesTotal = expenses.reduce((sum, e) => sum + e.amount, 0);

  const handleOpenModal = (expense?: Expense, prefillTripId?: string) => {
    if (expense) {
      setEditingExpense(expense);
      setFormData({
        project_id: expense.project_id,
        expense_category_id: expense.expense_category_id || '',
        trip_id: expense.trip_id || '',
        date: expense.date,
        payee: expense.payee,
        amount: String(expense.amount),
        description: expense.description || '',
        notes: expense.notes || '',
        payment_method: expense.payment_method || 'credit_card',
        is_reimbursable: expense.is_reimbursable,
      });
      if (expense.receipt_image_url) {
        setReceiptPreview(expense.receipt_image_url);
      }
    } else {
      setEditingExpense(null);
      setFormData({
        project_id: selectedTrip?.project_id || '',
        expense_category_id: '',
        trip_id: prefillTripId || selectedTrip?.id || '',
        date: selectedDate,
        payee: '',
        amount: '',
        description: '',
        notes: '',
        payment_method: 'credit_card',
        is_reimbursable: true,
      });
      setReceiptPreview(null);
    }
    setPendingReceipt(null);
    setPendingOcrData(null);
    setFormError('');
    setPolicyWarnings(null);
    setIsModalOpen(true);
  };

  const handleCopyExpense = (expense: Expense) => {
    setEditingExpense(null);
    setIsCopying(true);
    setFormData({
      project_id: expense.project_id,
      expense_category_id: expense.expense_category_id || '',
      trip_id: expense.trip_id || '',
      date: selectedDate,
      payee: expense.payee,
      amount: String(expense.amount),
      description: expense.description || '',
      notes: expense.notes || '',
      payment_method: expense.payment_method || 'credit_card',
      is_reimbursable: expense.is_reimbursable,
    });
    setReceiptPreview(null);
    setFormError('');
    setPolicyWarnings(null);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingExpense(null);
    setIsCopying(false);
    setReceiptPreview(null);
    setPendingReceipt(null);
    setPendingOcrData(null);
    setPolicyWarnings(null);
    setFormError('');
  };

  const checkPolicies = async (amount: number, categoryId?: string, date?: string) => {
    try {
      const result = await expensePoliciesAPI.check(amount, categoryId, date || selectedDate);
      setPolicyWarnings(result);
    } catch {
      // Policy check is optional, don't block
    }
  };

  const handleAmountBlur = () => {
    const amount = parseFloat(formData.amount);
    if (!isNaN(amount) && amount > 0) {
      checkPolicies(amount, formData.expense_category_id || undefined, formData.date);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.project_id) { setFormError('Please select a project'); return; }
    if (!formData.payee.trim()) { setFormError('Please enter a payee'); return; }
    const amount = parseFloat(formData.amount);
    if (isNaN(amount) || amount <= 0) { setFormError('Please enter a valid amount'); return; }

    setIsSubmitting(true);
    try {
      if (editingExpense) {
        const updateData: ExpenseUpdate = {
          project_id: formData.project_id,
          expense_category_id: formData.expense_category_id || undefined,
          trip_id: formData.trip_id || null,
          date: formData.date,
          payee: formData.payee.trim(),
          amount: parseFloat(amount.toFixed(2)),
          description: formData.description.trim() || undefined,
          notes: formData.notes.trim() || undefined,
          payment_method: formData.payment_method,
          is_reimbursable: formData.is_reimbursable,
        };
        await updateExpense(editingExpense.id, updateData);
      } else {
        const createData: ExpenseCreate = {
          project_id: formData.project_id,
          expense_category_id: formData.expense_category_id || undefined,
          trip_id: formData.trip_id || undefined,
          date: formData.date,
          payee: formData.payee.trim(),
          amount: parseFloat(amount.toFixed(2)),
          description: formData.description.trim() || undefined,
          notes: formData.notes.trim() || undefined,
          payment_method: formData.payment_method,
          is_reimbursable: formData.is_reimbursable,
        };
        const created = await createExpense(createData);
        // The create endpoint ignores receipt fields, so attach a snapped/uploaded
        // receipt (and its OCR result) to the new expense via a follow-up update.
        if (pendingReceipt) {
          await updateExpense(created.id, {
            receipt_image_url: pendingReceipt.image_url,
            receipt_thumbnail_url: pendingReceipt.thumbnail_url,
            ...(pendingOcrData ? { ocr_data: pendingOcrData } : {}),
          });
        }
      }
      handleCloseModal();
      refreshExpenses();
      // Refresh trip expenses if viewing a trip
      if (selectedTrip) {
        const updated = await tripsAPI.listExpenses(selectedTrip.id);
        setTripExpenses(updated);
        fetchTrips();
      }
    } catch (err: any) {
      const detail = err.response?.data?.detail;
      if (Array.isArray(detail)) {
        setFormError(detail.map((e: any) => `${e.loc?.join('.')}: ${e.msg}`).join(', ') || 'Failed to save expense');
      } else {
        setFormError(detail || 'Failed to save expense');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteClick = (expense: Expense) => {
    setDeletingExpense(expense);
    setIsDeleteModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!deletingExpense) return;
    setIsSubmitting(true);
    try {
      await deleteExpense(deletingExpense.id);
      setIsDeleteModalOpen(false);
      setDeletingExpense(null);
      refreshExpenses();
      // Keep the trip panel (list + totals) in sync when a trip expense is deleted.
      if (selectedTrip) {
        const updated = await tripsAPI.listExpenses(selectedTrip.id);
        setTripExpenses(updated);
        fetchTrips();
      }
    } catch {
      // Error handled by store
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleReceiptUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setIsUploadingReceipt(true);
    try {
      const result = await receiptsAPI.upload(file);
      setReceiptPreview(result.thumbnail_url || result.image_url);

      // If editing, update the expense with receipt URL immediately.
      // For a new expense, stash it so it's attached after create.
      if (editingExpense) {
        await updateExpense(editingExpense.id, {
          receipt_image_url: result.image_url,
          receipt_thumbnail_url: result.thumbnail_url,
        });
        setEditingExpense({
          ...editingExpense,
          receipt_image_url: result.image_url,
          receipt_thumbnail_url: result.thumbnail_url,
        });
      } else {
        setPendingReceipt({ image_url: result.image_url, thumbnail_url: result.thumbnail_url });
      }
    } catch (err: any) {
      setFormError(err.response?.data?.detail || 'Failed to upload receipt');
    } finally {
      setIsUploadingReceipt(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  // ─── Multi-page "Snap Receipt" capture ──────────────────────────────────
  // Snap pages one at a time (native camera), review, then combine into one
  // expense with OCR across all pages.

  const startReceiptCapture = (tripId?: string) => {
    setCapturePages([]);
    setCaptureTripId(tripId || null);
    setCaptureError('');
    setIsCaptureOpen(true);
    // Open the camera straight away for the first page.
    setTimeout(() => captureInputRef.current?.click(), 50);
  };

  const addCapturePage = () => {
    setCaptureError('');
    captureInputRef.current?.click();
  };

  const handleCaptureInput = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setIsCaptureUploading(true);
      setCaptureError('');
      try {
        const result = await receiptsAPI.upload(file);
        setCapturePages(prev => [...prev, { image_url: result.image_url, thumbnail_url: result.thumbnail_url }]);
      } catch (err: any) {
        setCaptureError(err.response?.data?.detail || 'Failed to upload page');
      } finally {
        setIsCaptureUploading(false);
      }
    }
    if (captureInputRef.current) captureInputRef.current.value = '';
  };

  const removeCapturePage = (idx: number) => {
    setCapturePages(prev => prev.filter((_, i) => i !== idx));
  };

  const cancelCapture = () => {
    setIsCaptureOpen(false);
    setCapturePages([]);
    setCaptureError('');
    setCaptureTripId(null);
  };

  // Open the expense form pre-filled with a combined receipt + its OCR result.
  const openExpenseFormWithReceipt = (
    combined: { image_url: string; thumbnail_url: string; ocr_data: any },
    tripId: string | null,
  ) => {
    const trip = tripId ? trips.find(t => t.id === tripId) : null;
    setEditingExpense(null);
    setIsCopying(false);
    setFormData({
      project_id: trip?.project_id || '',
      expense_category_id: '',
      trip_id: tripId || '',
      date: selectedDate,
      payee: '',
      amount: '',
      description: '',
      notes: '',
      payment_method: 'credit_card',
      is_reimbursable: true,
    });
    setReceiptPreview(combined.thumbnail_url);
    setPendingReceipt({ image_url: combined.image_url, thumbnail_url: combined.thumbnail_url });
    setPendingOcrData(combined.ocr_data || null);
    applyOCRFields(combined.ocr_data?.extracted_fields);
    setFormError('');
    setPolicyWarnings(null);
    setIsModalOpen(true);
  };

  // Auto-save a snapped receipt as an expense (used when OCR is confident and
  // the project is known from the trip). Returns the created expense.
  const autoSaveSnappedExpense = async (
    combined: { image_url: string; thumbnail_url: string; ocr_data: any },
    trip: BusinessTrip,
  ) => {
    const ex = combined.ocr_data?.extracted_fields;
    const validDate = ex?.date?.value && /^\d{4}-\d{2}-\d{2}$/.test(ex.date.value) ? ex.date.value : selectedDate;
    const created = await createExpense({
      project_id: trip.project_id,
      trip_id: trip.id,
      expense_category_id: matchCategoryId(ex?.category?.value),
      date: validDate,
      payee: ex?.payee?.value ? String(ex.payee.value) : 'Receipt',
      amount: Number(ex.amount.value),
      description: ex?.description?.value ? String(ex.description.value) : undefined,
      payment_method: 'credit_card',
      is_reimbursable: true,
    });
    await updateExpense(created.id, {
      receipt_image_url: combined.image_url,
      receipt_thumbnail_url: combined.thumbnail_url,
      ocr_data: combined.ocr_data,
    });
    if (selectedTrip && selectedTrip.id === trip.id) {
      const updated = await tripsAPI.listExpenses(trip.id);
      setTripExpenses(updated);
    }
    fetchTrips();
    refreshExpenses();
    return created;
  };

  const finishCapture = async () => {
    if (capturePages.length === 0) {
      cancelCapture();
      return;
    }
    setIsCombining(true);
    setCaptureError('');
    const tripId = captureTripId;
    try {
      const combined = await receiptsAPI.combine(capturePages.map(p => p.image_url));
      setIsCaptureOpen(false);
      setCapturePages([]);
      setCaptureTripId(null);

      // Auto-save when we can: OCR read a positive amount via the vision model
      // AND the project is known from the trip. Otherwise open the form to review.
      const ex = combined.ocr_data?.extracted_fields;
      const amountOk = ex?.amount?.value != null && Number(ex.amount.value) > 0;
      const visionOk = combined.ocr_data?.method === 'vision';
      const trip = tripId ? trips.find(t => t.id === tripId) : null;

      if (trip && amountOk && visionOk) {
        try {
          await autoSaveSnappedExpense(combined, trip);
          setSnapMessage(`Saved $${Number(ex.amount.value).toFixed(2)} — ${ex?.payee?.value || 'expense'}. Tap to edit if needed.`);
          return;
        } catch {
          // Save failed — fall back to the form so nothing is lost.
        }
      }
      openExpenseFormWithReceipt(combined, tripId);
    } catch (err: any) {
      setCaptureError(err.response?.data?.detail || 'Failed to combine pages');
    } finally {
      setIsCombining(false);
    }
  };

  // Apply OCR-extracted fields onto the expense form.
  const applyOCRFields = (extracted: any) => {
    if (!extracted) return;

    // Match a returned category name to one of the user's categories (by name).
    let matchedCategoryId = '';
    const catName = extracted.category?.value;
    if (catName) {
      const match = activeCategories.find(
        c => c.name.toLowerCase() === String(catName).toLowerCase()
      );
      if (match) matchedCategoryId = match.id;
    }

    // Only accept a strict YYYY-MM-DD date (fits the native date input).
    const ocrDate = extracted.date?.value;
    const validDate = typeof ocrDate === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(ocrDate)
      ? ocrDate
      : null;

    setFormData(prev => ({
      ...prev,
      payee: extracted.payee?.value || prev.payee,
      amount: extracted.amount?.value ? String(extracted.amount.value) : prev.amount,
      description: extracted.description?.value || prev.description,
      date: validDate || prev.date,
      expense_category_id: matchedCategoryId || prev.expense_category_id,
    }));
  };

  // Open the receipt viewer for the expense currently in the form (edit or snap)
  const openReceiptViewerForForm = () => {
    const imageUrl = editingExpense?.receipt_image_url || pendingReceipt?.image_url || receiptPreview || '';
    const ocr = editingExpense?.ocr_data || pendingOcrData || null;
    if (!imageUrl) return;
    setViewerOCRError('');
    setSplitConfirm(false);
    setViewingReceipt({
      imageUrl,
      ocr,
      expenseId: editingExpense?.id,
      imagePath: editingExpense?.receipt_image_url || pendingReceipt?.image_url,
    });
  };

  // Open the receipt viewer for a specific expense (e.g. a trip expense row)
  const openReceiptViewer = (expense: Expense) => {
    const imageUrl = expense.receipt_image_url || expense.receipt_thumbnail_url || '';
    if (!imageUrl) return;
    setViewerOCRError('');
    setSplitConfirm(false);
    setViewingReceipt({
      imageUrl,
      ocr: expense.ocr_data || null,
      expenseId: expense.id,
      imagePath: expense.receipt_image_url,
    });
  };

  const OCR_FIELD_LABELS: Record<string, string> = {
    payee: 'Payee', amount: 'Amount', date: 'Date', time: 'Time',
    description: 'Description', category: 'Category', currency: 'Currency',
  };

  // Format a 24h "HH:MM" receipt time as a friendly 12h string.
  const formatReceiptTime = (t?: string): string => {
    if (!t) return '';
    const [hStr, mStr] = t.split(':');
    const h = Number(hStr);
    const m = Number(mStr);
    if (Number.isNaN(h) || Number.isNaN(m)) return '';
    const ampm = h >= 12 ? 'PM' : 'AM';
    const h12 = ((h + 11) % 12) + 1;
    return `${h12}:${String(m).padStart(2, '0')} ${ampm}`;
  };

  const renderOcrFields = (fields?: Record<string, any>) => {
    if (!fields) return null;
    const entries = Object.entries(fields).filter(
      ([, v]) => v && typeof v === 'object' && 'value' in v && v.value !== '' && v.value != null
    );
    if (entries.length === 0) {
      return <p className="text-sm text-gray-500 dark:text-gray-400">No fields were extracted.</p>;
    }
    return (
      <dl className="grid grid-cols-3 gap-x-3 gap-y-1.5 text-sm">
        {entries.map(([k, v]) => (
          <React.Fragment key={k}>
            <dt className="col-span-1 text-gray-500 dark:text-gray-400">{OCR_FIELD_LABELS[k] || k}</dt>
            <dd className="col-span-2 text-gray-900 dark:text-white break-words">
              {k === 'amount' ? `$${Number(v.value).toFixed(2)}` : String(v.value)}
            </dd>
          </React.Fragment>
        ))}
      </dl>
    );
  };

  // Match a category name (from OCR) to one of the user's active categories.
  const matchCategoryId = (name?: string | null): string | undefined => {
    if (!name) return undefined;
    const m = activeCategories.find(c => c.name.toLowerCase() === String(name).toLowerCase());
    return m?.id;
  };

  // The breakdown rows for the receipt currently in the viewer: consolidated to
  // one row per (day, category) — so a hotel folio shows lodging and meals
  // broken out by day — and reconciled so the rows sum exactly to the total.
  const viewerBreakdown = (): { date: string | null; category: string | null; amount: number; description: string | null }[] => {
    const raw = viewingReceipt?.ocr?.breakdown;
    if (!Array.isArray(raw)) return [];

    const byKey = new Map<string, { date: string | null; category: string | null; amount: number; description: string | null }>();
    for (const x of raw) {
      if (!x || typeof x.amount !== 'number' || x.amount <= 0) continue;
      const date = (typeof x.date === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(x.date)) ? x.date : null;
      const key = `${date || ''}|${String(x.category || 'Uncategorized').toLowerCase()}`;
      const existing = byKey.get(key);
      if (existing) {
        existing.amount = Math.round((existing.amount + x.amount) * 100) / 100;
      } else {
        byKey.set(key, {
          date,
          category: x.category || null,
          amount: Math.round(x.amount * 100) / 100,
          description: x.description || null,
        });
      }
    }
    const rows = Array.from(byKey.values());
    if (rows.length === 0) return rows;

    rows.sort((a, b) =>
      (a.date || '').localeCompare(b.date || '')
      || (a.category || '').toLowerCase().localeCompare((b.category || '').toLowerCase())
    );

    // Reconcile to the saved expense's total so the split adds up exactly.
    const src = viewingReceipt?.expenseId
      ? [...tripExpenses, ...expenses].find(e => e.id === viewingReceipt.expenseId)
      : undefined;
    if (src) {
      const sum = rows.reduce((s, r) => s + r.amount, 0);
      const delta = Math.round((src.amount - sum) * 100) / 100;
      if (delta !== 0) {
        let idx = 0;
        for (let i = 1; i < rows.length; i++) if (rows[i].amount > rows[idx].amount) idx = i;
        const adjusted = Math.round((rows[idx].amount + delta) * 100) / 100;
        if (adjusted > 0) rows[idx] = { ...rows[idx], amount: adjusted };
      }
    }
    return rows;
  };

  // The source expense currently shown in the viewer (if any).
  const viewerSourceExpense = (): Expense | undefined =>
    viewingReceipt?.expenseId
      ? [...tripExpenses, ...expenses].find(e => e.id === viewingReceipt.expenseId)
      : undefined;

  // Enter split mode: seed editable rows from the AI breakdown (or one full-amount row).
  const beginSplit = () => {
    const src = viewerSourceExpense();
    const fallbackDate = src?.date || selectedDate;
    const bd = viewerBreakdown();
    const rows = bd.map(r => ({
      date: r.date || fallbackDate,
      category_id: matchCategoryId(r.category) || '',
      description: r.description || '',
      amount: r.amount.toFixed(2),
    }));
    if (src) {
      if (rows.length === 0) {
        rows.push({ date: fallbackDate, category_id: src.expense_category_id || '', description: src.description || '', amount: src.amount.toFixed(2) });
      }
      while (rows.length < 2) {
        rows.push({ date: fallbackDate, category_id: '', description: '', amount: '0.00' });
      }
    }
    setSplitRows(rows);
    setViewerOCRError('');
    setSplitConfirm(true);
  };

  const updateSplitRow = (idx: number, patch: Partial<{ date: string; category_id: string; description: string; amount: string }>) => {
    setSplitRows(prev => prev.map((r, i) => (i === idx ? { ...r, ...patch } : r)));
  };
  const addSplitRow = () => {
    const src = viewerSourceExpense();
    setSplitRows(prev => [...prev, { date: src?.date || selectedDate, category_id: '', description: '', amount: '0.00' }]);
  };
  const removeSplitRow = (idx: number) => {
    setSplitRows(prev => prev.filter((_, i) => i !== idx));
  };
  const splitRowsTotal = () => splitRows.reduce((s, r) => s + (parseFloat(r.amount) || 0), 0);

  // Create one expense per split row (dated per row) and remove the original.
  const handleSplitReceipt = async () => {
    if (!viewingReceipt?.expenseId) return;
    const items = splitRows
      .map(r => ({
        amount: parseFloat(r.amount) || 0,
        expense_category_id: r.category_id || undefined,
        description: r.description.trim() || undefined,
        date: /^\d{4}-\d{2}-\d{2}$/.test(r.date) ? r.date : undefined,
      }))
      .filter(i => i.amount > 0);
    if (items.length < 2) {
      setViewerOCRError('Add at least two lines with positive amounts to split.');
      return;
    }
    setIsSplitting(true);
    setViewerOCRError('');
    try {
      await expensesAPI.split(viewingReceipt.expenseId, items);
      setViewingReceipt(null);
      setSplitConfirm(false);
      setSplitRows([]);
      if (selectedTrip) {
        const updated = await tripsAPI.listExpenses(selectedTrip.id);
        setTripExpenses(updated);
        fetchTrips();
      }
      refreshExpenses();
    } catch (err: any) {
      setViewerOCRError(err.response?.data?.detail || 'Failed to split expense');
    } finally {
      setIsSplitting(false);
    }
  };

  // Map OCR-extracted fields to an ExpenseUpdate payload (only fields OCR found).
  const ocrToExpenseUpdate = (extracted: any): ExpenseUpdate => {
    const upd: ExpenseUpdate = {};
    if (!extracted) return upd;
    if (extracted.payee?.value) upd.payee = String(extracted.payee.value);
    if (extracted.amount?.value != null && !isNaN(Number(extracted.amount.value))) {
      upd.amount = Number(extracted.amount.value);
    }
    if (extracted.date?.value && /^\d{4}-\d{2}-\d{2}$/.test(extracted.date.value)) {
      upd.date = extracted.date.value;
    }
    if (extracted.description?.value) upd.description = String(extracted.description.value);
    const catName = extracted.category?.value;
    if (catName) {
      const m = activeCategories.find(c => c.name.toLowerCase() === String(catName).toLowerCase());
      if (m) upd.expense_category_id = m.id;
    }
    return upd;
  };

  // Run (or re-run) OCR from the receipt viewer and show the result there.
  const handleViewerRunOCR = async () => {
    if (!viewingReceipt) return;
    setViewerOCRError('');
    setIsProcessingOCR(true);
    try {
      let ocr: any = null;

      if (viewingReceipt.expenseId) {
        // Existing expense: backend runs OCR and persists ocr_data on the row.
        ocr = await receiptsAPI.processOCR(viewingReceipt.expenseId);
        const id = viewingReceipt.expenseId;

        if (editingExpense && editingExpense.id === id) {
          // Being edited: fill the form so the user reviews/saves the change.
          setEditingExpense(prev => (prev ? { ...prev, ocr_data: ocr } : prev));
          applyOCRFields(ocr?.extracted_fields);
          setTripExpenses(prev => prev.map(e => (e.id === id ? { ...e, ocr_data: ocr } : e)));
        } else {
          // Not being edited (trip row / list): persist the corrected fields
          // directly onto the saved expense so the amount, payee, etc. update.
          const upd = ocrToExpenseUpdate(ocr?.extracted_fields);
          if (Object.keys(upd).length > 0) {
            await updateExpense(id, upd);
          }
          if (selectedTrip) {
            const updated = await tripsAPI.listExpenses(selectedTrip.id);
            setTripExpenses(updated);
            fetchTrips();
          }
          refreshExpenses();
        }
      } else if (viewingReceipt.imagePath) {
        // Not-yet-saved snapped receipt: OCR the uploaded file directly.
        ocr = await receiptsAPI.processOCRFile(viewingReceipt.imagePath);
        setPendingOcrData(ocr);
        applyOCRFields(ocr?.extracted_fields);
      }

      if (ocr) setViewingReceipt(prev => (prev ? { ...prev, ocr } : prev));
    } catch (err: any) {
      setViewerOCRError(err.response?.data?.detail || 'OCR processing failed');
    } finally {
      setIsProcessingOCR(false);
    }
  };

  // Calendar generation
  const generateCalendarDays = () => {
    const selected = new Date(selectedDate + 'T00:00:00');
    const year = selected.getFullYear();
    const month = selected.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const todayStr = getLocalDateString();
    const days: { date: string; isCurrentMonth: boolean; isSelected: boolean; isToday: boolean }[] = [];

    const startDayOfWeek = firstDay.getDay();
    for (let i = startDayOfWeek - 1; i >= 0; i--) {
      const date = new Date(year, month, -i);
      const dateStr = getLocalDateString(date);
      days.push({ date: dateStr, isCurrentMonth: false, isSelected: false, isToday: dateStr === todayStr });
    }
    for (let i = 1; i <= lastDay.getDate(); i++) {
      const date = new Date(year, month, i);
      const dateStr = getLocalDateString(date);
      days.push({ date: dateStr, isCurrentMonth: true, isSelected: dateStr === selectedDate, isToday: dateStr === todayStr });
    }
    const remainingDays = 42 - days.length;
    for (let i = 1; i <= remainingDays; i++) {
      const date = new Date(year, month + 1, i);
      const dateStr = getLocalDateString(date);
      days.push({ date: dateStr, isCurrentMonth: false, isSelected: false, isToday: dateStr === todayStr });
    }
    return days;
  };

  const navigateMonth = (direction: 'prev' | 'next') => {
    const current = new Date(selectedDate + 'T00:00:00');
    current.setMonth(current.getMonth() + (direction === 'prev' ? -1 : 1));
    setSelectedDate(getLocalDateString(current));
  };

  // ─── Trip handlers ──────────────────────────────────────────────────────

  const handleOpenTripModal = async (trip?: BusinessTrip) => {
    if (trip) {
      setEditingTrip(trip);
      setTripFormData({
        project_id: trip.project_id,
        subproject_id: trip.subproject_id || '',
        name: trip.name,
        destination: trip.destination || '',
        start_date: trip.start_date,
        end_date: trip.end_date,
        purpose: trip.purpose || '',
        status: trip.status,
      });
      // Load subprojects for the trip's project
      if (trip.project_id) {
        const sps = await subprojectsAPI.list(trip.project_id);
        setTripSubprojects(sps.filter(s => s.is_active));
      }
    } else {
      setEditingTrip(null);
      setTripFormData({ project_id: '', subproject_id: '', name: '', destination: '', start_date: '', end_date: '', purpose: '', status: 'active' });
      setTripSubprojects([]);
    }
    setTripFormError('');
    setIsTripModalOpen(true);
  };

  const handleTripSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!tripFormData.project_id) { setTripFormError('Please select a project'); return; }
    if (!tripFormData.name.trim()) { setTripFormError('Please enter a trip name'); return; }
    if (!tripFormData.start_date) { setTripFormError('Please select a start date'); return; }
    if (!tripFormData.end_date) { setTripFormError('Please select an end date'); return; }
    if (tripFormData.end_date < tripFormData.start_date) { setTripFormError('End date must be on or after start date'); return; }

    setIsTripSubmitting(true);
    try {
      const payload = {
        project_id: tripFormData.project_id,
        subproject_id: tripFormData.subproject_id || null,
        name: tripFormData.name.trim(),
        destination: tripFormData.destination.trim() || undefined,
        start_date: tripFormData.start_date,
        end_date: tripFormData.end_date,
        purpose: tripFormData.purpose.trim() || undefined,
        status: tripFormData.status as any,
      };
      if (editingTrip) {
        const updated = await updateTrip(editingTrip.id, payload);
        if (selectedTrip?.id === editingTrip.id) setSelectedTrip(updated);
      } else {
        const created = await createTrip(payload);
        setSelectedTrip(created);
        setTripExpenses([]);
      }
      setIsTripModalOpen(false);
    } catch (err: any) {
      setTripFormError(err.message || 'Failed to save trip');
    } finally {
      setIsTripSubmitting(false);
    }
  };

  const handleSelectTrip = async (trip: BusinessTrip) => {
    setSelectedTrip(trip);
    const expenses = await tripsAPI.listExpenses(trip.id);
    setTripExpenses(expenses);
  };


  const handleConfirmDeleteTrip = async () => {
    if (!tripToDelete) return;
    setIsTripSubmitting(true);
    try {
      await deleteTrip(tripToDelete.id);
      if (selectedTrip?.id === tripToDelete.id) { setSelectedTrip(null); setTripExpenses([]); }
      setTripToDelete(null);
      setIsDeletingTrip(false);
    } catch { } finally {
      setIsTripSubmitting(false);
    }
  };

  const EXPORT_LABELS: Record<'pdf' | 'csv' | 'docx', string> = { pdf: 'PDF', csv: 'CSV', docx: 'Word' };

  // Turn a trip name into a safe download filename (strip characters the OS/browser
  // dislike, collapse whitespace); fall back to "trip_export" if it's empty.
  const tripFileName = (name: string, format: string) => {
    const safe = (name || '').replace(/[\\/:*?"<>|]+/g, ' ').replace(/\s+/g, ' ').trim();
    return `${safe || 'trip_export'}.${format}`;
  };

  const handleExportTrip = async (tripId: string, format: 'pdf' | 'csv' | 'docx', tripName: string) => {
    setExportMenuOpen(false);
    if (exportBusy) return;  // one report at a time
    // Route through tripsAPI (apiClient) so the auth interceptor supplies a
    // fresh token and refreshes on 401 — a raw fetch with a stale token would
    // save the "Could not validate credentials" JSON as the file.
    setExportBusy(EXPORT_LABELS[format]);  // show progress until the download is ready
    try {
      const blob = await tripsAPI.exportBlob(tripId, format);
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = tripFileName(tripName, format);  // save as the trip's name
      a.click();
      URL.revokeObjectURL(a.href);
    } catch {
      setSnapMessage('Export failed — please try again.');
    } finally {
      setExportBusy('');  // clear the indicator whether it succeeded or failed
    }
  };

  // Open the PDF report in a new tab and trigger the browser print dialog.
  const handlePrintTrip = async (tripId: string) => {
    setExportMenuOpen(false);
    if (exportBusy) return;
    setExportBusy('PDF');
    try {
      const blob = await tripsAPI.exportBlob(tripId, 'pdf');
      const url = URL.createObjectURL(blob);
      const w = window.open(url, '_blank');
      if (w) {
        w.addEventListener('load', () => {
          try { w.focus(); w.print(); } catch { /* user can print manually */ }
        });
      }
      // Revoke later so the new tab has time to load.
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch {
      setSnapMessage('Could not open the report — please try again.');
    } finally {
      setExportBusy('');
    }
  };

  const tripStatusLabel: Record<string, string> = { active: 'Active', completed: 'Completed', submitted: 'Submitted' };
  const tripStatusColor: Record<string, string> = {
    active: 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300',
    completed: 'bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300',
    submitted: 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-300',
  };

  // A single expense row, shared by the Day and All views.
  const renderExpenseRow = (expense: Expense) => (
    <div key={expense.id} className="px-6 py-4 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
      <div className="flex items-start justify-between">
        <div className="flex-1 min-w-0">
          <div className="flex items-center space-x-2 flex-wrap gap-y-1">
            {expense.category_icon && (
              <span className="text-base">{expense.category_icon}</span>
            )}
            <span className="text-sm font-medium text-gray-900 dark:text-white">{expense.payee}</span>
            <span className="text-gray-300 dark:text-gray-600">|</span>
            <span className="text-sm text-gray-600 dark:text-gray-400">
              {expense.client_name} / {expense.project_name}
            </span>
            {expense.receipt_image_url && (
              <button
                type="button"
                onClick={() => openReceiptViewer(expense)}
                title="View receipt"
                className="inline-flex items-center px-1.5 py-0.5 rounded text-xs bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 hover:bg-blue-200 dark:hover:bg-blue-800 transition-colors"
              >
                Receipt
              </button>
            )}
          </div>
          {expense.description && (
            <p className="mt-1 text-sm text-gray-700 dark:text-gray-300">{expense.description}</p>
          )}
          <div className="mt-1 flex items-center space-x-2 text-xs text-gray-500 dark:text-gray-400">
            <span>{PAYMENT_METHOD_LABELS[expense.payment_method] || expense.payment_method}</span>
            {receiptTime(expense) && (
              <span className="inline-flex items-center gap-0.5" title="Receipt time">
                <ClockIcon className="h-3 w-3" />{formatReceiptTime(receiptTime(expense))}
              </span>
            )}
            {expense.is_reimbursable && (
              <span className="inline-flex items-center px-1.5 py-0.5 rounded bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300">
                Reimbursable
              </span>
            )}
          </div>
        </div>
        <div className="ml-4 flex items-center space-x-3">
          {expense.category_name && (
            <span
              className="inline-flex items-center px-2 py-1 rounded text-xs font-medium"
              style={{
                backgroundColor: `${expense.category_color || '#6B7280'}20`,
                color: expense.category_color || '#6B7280',
              }}
            >
              {expense.category_name}
            </span>
          )}
          <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
            ${expense.amount.toFixed(2)}
          </span>
          <button
            onClick={() => handleOpenModal(expense)}
            className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
            title="Edit"
          >
            <PencilIcon className="h-4 w-4" />
          </button>
          <button
            onClick={() => handleCopyExpense(expense)}
            className="p-2 text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
            title="Copy Expense"
          >
            <DocumentDuplicateIcon className="h-4 w-4" />
          </button>
          <button
            onClick={() => handleDeleteClick(expense)}
            className="p-2 text-gray-400 hover:text-red-600 dark:hover:text-red-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
            title="Delete"
          >
            <TrashIcon className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );

  // Group a trip's expenses by date (oldest first); within a day, order by
  // category then receipt time — an itemized-by-category ledger per day.
  const tripExpensesByDate = (): { date: string; items: Expense[]; subtotal: number }[] => {
    const sorted = [...tripExpenses].sort((a, b) =>
      a.date.localeCompare(b.date)
      || (a.category_name || '~').toLowerCase().localeCompare((b.category_name || '~').toLowerCase())
      || (receiptTime(a) || '99:99').localeCompare(receiptTime(b) || '99:99')
    );
    const groups: { date: string; items: Expense[]; subtotal: number }[] = [];
    for (const e of sorted) {
      let g = groups.find(x => x.date === e.date);
      if (!g) { g = { date: e.date, items: [], subtotal: 0 }; groups.push(g); }
      g.items.push(e);
      g.subtotal += e.amount;
    }
    return groups;
  };

  // A trip expense row — ledger style: time · category · payee, with the
  // receipt kept as a small secondary thumbnail.
  const renderTripExpenseRow = (expense: Expense) => (
    <div
      key={expense.id}
      className="flex items-center gap-3 bg-gray-50 dark:bg-gray-700/40 rounded-lg px-3 py-2.5 border border-gray-100 dark:border-gray-700"
    >
      <div className="w-12 flex-shrink-0 text-right text-xs text-gray-400 dark:text-gray-500 tabular-nums">
        {receiptTime(expense) ? formatReceiptTime(receiptTime(expense)) : '—'}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          {expense.category_name ? (
            <span
              className="text-xs px-2 py-0.5 rounded-full text-white font-medium"
              style={{ backgroundColor: expense.category_color || '#6B7280' }}
            >
              {expense.category_name}
            </span>
          ) : (
            <span className="text-xs px-2 py-0.5 rounded-full bg-gray-200 dark:bg-gray-600 text-gray-600 dark:text-gray-300">Uncategorized</span>
          )}
          <span className="text-sm text-gray-900 dark:text-white truncate">{expense.payee}</span>
        </div>
        {expense.description && (
          <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 truncate">{expense.description}</div>
        )}
      </div>
      <span className="font-semibold text-gray-900 dark:text-white text-sm tabular-nums">${expense.amount.toFixed(2)}</span>
      {(expense.receipt_thumbnail_url || expense.receipt_image_url) && (
        <button
          type="button"
          onClick={() => openReceiptViewer(expense)}
          title="View receipt & OCR"
          className="flex-shrink-0"
        >
          <img
            src={getReceiptUrl(expense.receipt_thumbnail_url || expense.receipt_image_url)}
            alt="Receipt"
            className="h-8 w-8 object-cover rounded border border-gray-200 dark:border-gray-600 hover:opacity-90 transition-opacity cursor-zoom-in"
          />
        </button>
      )}
      <div className="flex items-center gap-1 flex-shrink-0">
        <button
          onClick={() => handleOpenModal(expense)}
          className="p-1.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
          title="Edit"
        >
          <PencilIcon className="h-3.5 w-3.5" />
        </button>
        <button
          onClick={() => handleDeleteClick(expense)}
          className="p-1.5 text-gray-400 hover:text-red-500 rounded hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
          title="Delete expense"
        >
          <TrashIcon className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  );

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Trips &amp; Expenses</h1>
          <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
            {trips.length} trip{trips.length !== 1 ? 's' : ''}
          </p>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={() => setIsPolicyManagerOpen(true)}
            className="inline-flex items-center px-3 py-2 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 text-sm font-medium rounded-lg transition-colors"
            title="Expense Policies"
          >
            <Cog6ToothIcon className="h-4 w-4" />
          </button>
          <button
            onClick={() => handleOpenTripModal()}
            disabled={activeProjects.length === 0}
            className="inline-flex items-center px-4 py-2 bg-green-600 hover:bg-green-700 disabled:bg-gray-400 text-white text-sm font-medium rounded-lg transition-colors"
          >
            <PlusIcon className="h-5 w-5 mr-2" />
            New Trip
          </button>
        </div>
      </div>

      {activeProjects.length === 0 && (
        <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
          <p className="text-sm text-yellow-800 dark:text-yellow-200">
            You need to create a client and project before you can create trips and log expenses.
          </p>
        </div>
      )}

      {/* Calendar Dropdown (unused in trip-only view) */}
      {false && (<>
      {/* Calendar Dropdown */}
      {expenseView === 'day' && showCalendar && (
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 p-4 max-w-sm">
          <div className="flex items-center justify-between mb-4">
            <button onClick={() => navigateMonth('prev')} className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors">
              <ChevronLeftIcon className="h-5 w-5" />
            </button>
            <span className="text-sm font-medium text-gray-900 dark:text-white">
              {new Date(selectedDate + 'T00:00:00').toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
            </span>
            <button onClick={() => navigateMonth('next')} className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors">
              <ChevronRightIcon className="h-5 w-5" />
            </button>
          </div>
          <div className="grid grid-cols-7 gap-1 text-center text-xs font-medium text-gray-500 dark:text-gray-400 mb-2">
            {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map(day => (<div key={day} className="py-1">{day}</div>))}
          </div>
          <div className="grid grid-cols-7 gap-1">
            {generateCalendarDays().map((day, i) => (
              <button
                key={i}
                onClick={() => { setSelectedDate(day.date); setShowCalendar(false); }}
                className={`p-2 text-sm rounded transition-colors ${
                  day.isSelected ? 'bg-green-600 text-white'
                    : day.isToday ? 'bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-300'
                    : day.isCurrentMonth ? 'text-gray-900 dark:text-white hover:bg-gray-100 dark:hover:bg-gray-700'
                    : 'text-gray-400 dark:text-gray-600 hover:bg-gray-50 dark:hover:bg-gray-800'
                }`}
              >
                {new Date(day.date + 'T00:00:00').getDate()}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* No projects warning */}
      {activeProjects.length === 0 && (
        <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
          <p className="text-sm text-yellow-800 dark:text-yellow-200">
            You need to create a client and project before you can log expenses.
          </p>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
          <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
          <button onClick={clearError} className="text-sm text-red-800 dark:text-red-300 underline mt-1">Dismiss</button>
        </div>
      )}

      {/* Summary Card */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-5">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <div className="p-3 bg-green-100 dark:bg-green-900 rounded-lg">
              <BanknotesIcon className="h-6 w-6 text-green-600 dark:text-green-400" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                {expenseView === 'all' ? 'Total (all expenses)' : 'Daily Total'}
              </p>
              <p className="text-2xl font-semibold text-gray-900 dark:text-white">
                ${(expenseView === 'all' ? allExpensesTotal : dailyTotal).toFixed(2)}
              </p>
            </div>
          </div>
          <div className="text-right">
            <p className="text-sm text-gray-500 dark:text-gray-400">{expenses.length} expense{expenses.length !== 1 ? 's' : ''}</p>
          </div>
        </div>
      </div>

      {/* Expenses List */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700">
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Expenses</h2>
        </div>

        {isLoading ? (
          <div className="p-6 flex items-center justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600"></div>
          </div>
        ) : expenses.length > 0 ? (
          expenseView === 'all' ? (
            /* Chronological, grouped by date (oldest first) */
            <div>
              {groupedByDate.map((group) => (
                <div key={group.date}>
                  <div className="sticky top-0 z-10 flex items-center justify-between px-6 py-2 bg-gray-50 dark:bg-gray-700/40 border-b border-gray-200 dark:border-gray-700">
                    <span className="text-sm font-semibold text-gray-700 dark:text-gray-300">{formatDate(group.date)}</span>
                    <span className="text-sm font-medium text-gray-500 dark:text-gray-400">${group.subtotal.toFixed(2)}</span>
                  </div>
                  <div className="divide-y divide-gray-200 dark:divide-gray-700">
                    {group.items.map((expense) => renderExpenseRow(expense))}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="divide-y divide-gray-200 dark:divide-gray-700">
              {expenses.map((expense) => renderExpenseRow(expense))}
            </div>
          )
        ) : (
          <div className="px-6 py-12 text-center">
            <BanknotesIcon className="mx-auto h-12 w-12 text-gray-400 dark:text-gray-600" />
            <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">No expenses</h3>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              {expenseView === 'all' ? 'No expenses recorded yet.' : 'No expenses recorded for this day.'}
            </p>
            {activeProjects.length > 0 && (
              <button
                onClick={() => handleOpenModal()}
                className="mt-4 inline-flex items-center px-4 py-2 bg-green-600 hover:bg-green-700 text-white text-sm font-medium rounded-lg transition-colors"
              >
                <PlusIcon className="h-5 w-5 mr-2" />
                Add Expense
              </button>
            )}
          </div>
        )}
      </div>

      </>)}

      {/* Mileage Tab Content */}
      {activeTab === 'mileage' && <MileageSection />}

      {/* Per Diem Tab Content */}
      {activeTab === 'per-diem' && <PerDiemSection />}

      {/* Trips Tab Content */}
      {activeTab === 'trips' && (
        <div className="flex flex-col lg:flex-row gap-6 min-h-0">
          {/* Trip list — full width on mobile; hidden on mobile once a trip is selected (drill-down) */}
          <div className={`w-full lg:w-80 flex-shrink-0 space-y-3 ${selectedTrip ? 'hidden lg:block' : ''}`}>
            {trips.length === 0 ? (
              <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-8 text-center">
                <BriefcaseIcon className="h-10 w-10 text-gray-400 mx-auto mb-3" />
                <p className="text-sm text-gray-500 dark:text-gray-400">No trips yet.</p>
                <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">Click "New Trip" to create one.</p>
              </div>
            ) : (
              trips.map((trip) => (
                <div
                  key={trip.id}
                  className={`bg-white dark:bg-gray-800 rounded-xl border transition-colors ${
                    selectedTrip?.id === trip.id
                      ? 'border-green-500 ring-1 ring-green-500'
                      : 'border-gray-200 dark:border-gray-700 hover:border-green-400'
                  }`}
                >
                  {/* Clickable body — selects trip */}
                  <button
                    className="w-full text-left p-4 space-y-2"
                    onClick={() => handleSelectTrip(trip)}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <span className="font-medium text-gray-900 dark:text-white text-sm leading-snug">{trip.name}</span>
                      <span className={`flex-shrink-0 text-xs px-2 py-0.5 rounded-full font-medium ${tripStatusColor[trip.status]}`}>
                        {tripStatusLabel[trip.status]}
                      </span>
                    </div>
                    {trip.destination && (
                      <div className="flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400">
                        <MapPinIcon className="h-3.5 w-3.5" />{trip.destination}
                      </div>
                    )}
                    {trip.subproject_name && (
                      <div className="text-xs text-gray-500 dark:text-gray-400">{trip.subproject_name}</div>
                    )}
                    <div className="flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400">
                      <CalendarIcon className="h-3.5 w-3.5" />
                      {new Date(trip.start_date + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                      {' – '}
                      {new Date(trip.end_date + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                    </div>
                    <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
                      <span>{trip.expense_count} expense{trip.expense_count !== 1 ? 's' : ''}</span>
                      <span className="font-semibold text-gray-900 dark:text-white">${trip.total_amount.toFixed(2)}</span>
                    </div>
                  </button>
                  {/* Always-visible edit / delete actions */}
                  <div className="flex items-center gap-1 px-4 pb-3 border-t border-gray-100 dark:border-gray-700 pt-2">
                    <button
                      onClick={(e) => { e.stopPropagation(); handleOpenTripModal(trip); }}
                      className="inline-flex items-center gap-1 px-2 py-1 text-xs text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors"
                    >
                      <PencilIcon className="h-3.5 w-3.5" />Edit
                    </button>
                    <button
                      onClick={(e) => { e.stopPropagation(); setTripToDelete(trip); setIsDeletingTrip(true); }}
                      className="inline-flex items-center gap-1 px-2 py-1 text-xs text-gray-500 dark:text-gray-400 hover:text-red-600 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded transition-colors"
                    >
                      <TrashIcon className="h-3.5 w-3.5" />Delete
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>

          {/* Trip detail panel */}
          {selectedTrip ? (
            <div className="flex-1 min-w-0 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 flex flex-col">
              {/* Trip header */}
              <div className="p-5 border-b border-gray-200 dark:border-gray-700">
                {/* Mobile back to trip list */}
                <button
                  onClick={() => { setSelectedTrip(null); setTripExpenses([]); }}
                  className="lg:hidden inline-flex items-center gap-1 mb-3 -ml-1 px-2 py-1 text-sm font-medium text-green-600 dark:text-green-400 hover:bg-green-50 dark:hover:bg-green-900/20 rounded-lg transition-colors"
                >
                  <ChevronLeftIcon className="h-4 w-4" />
                  All trips
                </button>
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h2 className="text-lg font-semibold text-gray-900 dark:text-white">{selectedTrip.name}</h2>
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${tripStatusColor[selectedTrip.status]}`}>
                        {tripStatusLabel[selectedTrip.status]}
                      </span>
                    </div>
                    <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-sm text-gray-500 dark:text-gray-400">
                      <span>{selectedTrip.project_name} · {selectedTrip.client_name}</span>
                      {selectedTrip.destination && (
                        <span className="flex items-center gap-1"><MapPinIcon className="h-4 w-4" />{selectedTrip.destination}</span>
                      )}
                      <span className="flex items-center gap-1">
                        <CalendarIcon className="h-4 w-4" />
                        {new Date(selectedTrip.start_date + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                        {' – '}
                        {new Date(selectedTrip.end_date + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                      </span>
                    </div>
                    {selectedTrip.purpose && (
                      <p className="mt-1 text-sm text-gray-600 dark:text-gray-300 italic">{selectedTrip.purpose}</p>
                    )}
                  </div>
                  <div className="flex items-center flex-wrap gap-2 sm:flex-shrink-0">
                    <button
                      onClick={() => startReceiptCapture(selectedTrip.id)}
                      className="inline-flex items-center px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-xs font-medium rounded-lg transition-colors"
                      title="Snap a receipt photo to add an expense (with OCR)"
                    >
                      <CameraIcon className="h-4 w-4 mr-1" />Snap Receipt
                    </button>
                    <button
                      onClick={() => handleOpenModal(undefined, selectedTrip.id)}
                      className="inline-flex items-center px-3 py-1.5 bg-green-600 hover:bg-green-700 text-white text-xs font-medium rounded-lg transition-colors"
                    >
                      <PlusIcon className="h-4 w-4 mr-1" />Add Expense
                    </button>
                    <div className="relative">
                      <button
                        onClick={() => setExportMenuOpen(o => !o)}
                        disabled={!!exportBusy}
                        className="inline-flex items-center px-3 py-1.5 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 text-xs font-medium rounded-lg transition-colors disabled:opacity-60 disabled:cursor-wait"
                        title="Export or print the expense report"
                      >
                        {exportBusy ? (
                          <>
                            <svg className="animate-spin h-4 w-4 mr-1.5" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                            </svg>
                            Generating {exportBusy}…
                          </>
                        ) : (
                          <><ArrowDownTrayIcon className="h-4 w-4 mr-1" />Export</>
                        )}
                      </button>
                      {exportMenuOpen && (
                        <>
                          <div className="fixed inset-0 z-10" onClick={() => setExportMenuOpen(false)} />
                          <div className="absolute right-0 mt-1 w-52 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 py-1 z-20">
                            <button onClick={() => handlePrintTrip(selectedTrip.id)} className="flex items-center w-full px-3 py-2 text-xs text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors">
                              <PrinterIcon className="h-4 w-4 mr-2" />Print report
                            </button>
                            <button onClick={() => handleExportTrip(selectedTrip.id, 'pdf', selectedTrip.name)} className="flex items-center w-full px-3 py-2 text-xs text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors">
                              <ArrowDownTrayIcon className="h-4 w-4 mr-2" />PDF (with receipts)
                            </button>
                            <button onClick={() => handleExportTrip(selectedTrip.id, 'docx', selectedTrip.name)} className="flex items-center w-full px-3 py-2 text-xs text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors">
                              <DocumentTextIcon className="h-4 w-4 mr-2" />Word (with receipts)
                            </button>
                            <div className="border-t border-gray-200 dark:border-gray-700 my-1" />
                            <button onClick={() => handleExportTrip(selectedTrip.id, 'csv', selectedTrip.name)} className="flex items-center w-full px-3 py-2 text-xs text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors">
                              <ArrowDownTrayIcon className="h-4 w-4 mr-2" />CSV (data only)
                            </button>
                          </div>
                        </>
                      )}
                    </div>
                    <button
                      onClick={() => handleOpenTripModal(selectedTrip)}
                      className="p-1.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                      title="Edit trip"
                    >
                      <PencilIcon className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => { setTripToDelete(selectedTrip); setIsDeletingTrip(true); }}
                      className="p-1.5 text-gray-400 hover:text-red-500 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                      title="Delete trip"
                    >
                      <TrashIcon className="h-4 w-4" />
                    </button>
                  </div>
                </div>
                {/* Trip total */}
                <div className="mt-3 flex items-center gap-4 text-sm">
                  <span className="text-gray-500 dark:text-gray-400">{tripExpenses.length} expense{tripExpenses.length !== 1 ? 's' : ''}</span>
                  <span className="font-bold text-lg text-gray-900 dark:text-white">
                    Total: ${tripExpenses.reduce((s, e) => s + e.amount, 0).toFixed(2)}
                  </span>
                </div>
              </div>

              {/* Trip expense list */}
              <div className="flex-1 overflow-y-auto p-4 space-y-2">
                {tripExpenses.length === 0 ? (
                  <div className="text-center py-10">
                    <BanknotesIcon className="h-8 w-8 text-gray-300 mx-auto mb-2" />
                    <p className="text-sm text-gray-500 dark:text-gray-400">No expenses yet.</p>
                    <div className="mt-3 flex items-center justify-center gap-4">
                      <button
                        onClick={() => startReceiptCapture(selectedTrip.id)}
                        className="inline-flex items-center text-sm text-blue-600 dark:text-blue-400 hover:underline"
                      >
                        <CameraIcon className="h-4 w-4 mr-1" />Snap a receipt
                      </button>
                      <button
                        onClick={() => handleOpenModal(undefined, selectedTrip.id)}
                        className="text-sm text-green-600 dark:text-green-400 hover:underline"
                      >
                        Add manually
                      </button>
                    </div>
                  </div>
                ) : (
                  tripExpensesByDate().map((group) => (
                    <div key={group.date} className="space-y-2">
                      <div className="flex items-center justify-between px-1 pt-1 border-b border-gray-200 dark:border-gray-700 pb-1">
                        <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                          {new Date(group.date + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' })}
                        </h4>
                        <span className="text-sm font-medium text-gray-500 dark:text-gray-400 tabular-nums">${group.subtotal.toFixed(2)}</span>
                      </div>
                      {group.items.map((expense) => renderTripExpenseRow(expense))}
                    </div>
                  ))
                )}
              </div>
            </div>
          ) : (
            <div className="hidden lg:flex flex-1 items-center justify-center text-gray-400 dark:text-gray-500">
              <div className="text-center">
                <BriefcaseIcon className="h-12 w-12 mx-auto mb-3 opacity-30" />
                <p className="text-sm">Select a trip to view its expenses</p>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Expense Policy Manager */}
      <ExpensePolicyManager
        isOpen={isPolicyManagerOpen}
        onClose={() => setIsPolicyManagerOpen(false)}
      />

      {/* Add/Edit Expense Modal */}
      <Modal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        title={editingExpense ? 'Edit Expense' : isCopying ? 'Copy Expense' : 'Add Expense'}
        size="lg"
      >
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {/* Project */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Project</label>
              <select
                value={formData.project_id}
                onChange={(e) => setFormData({ ...formData, project_id: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-green-500 focus:border-transparent"
              >
                <option value="">Select a project...</option>
                {groupedProjects.map((group) => (
                  <optgroup key={group.client.id} label={group.client.name}>
                    {group.projects.map((project) => (
                      <option key={project.id} value={project.id}>{project.name}</option>
                    ))}
                  </optgroup>
                ))}
              </select>
            </div>

            {/* Category */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Category</label>
              <select
                value={formData.expense_category_id}
                onChange={(e) => setFormData({ ...formData, expense_category_id: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-green-500 focus:border-transparent"
              >
                <option value="">Select a category...</option>
                {activeCategories.map((cat) => (
                  <option key={cat.id} value={cat.id}>{cat.icon} {cat.name}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Trip (optional) */}
          {trips.length > 0 && (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Business Trip <span className="text-gray-400 font-normal">(optional)</span>
              </label>
              <select
                value={formData.trip_id}
                onChange={(e) => setFormData({ ...formData, trip_id: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-green-500 focus:border-transparent"
              >
                <option value="">No trip (ad hoc expense)</option>
                {trips.map((trip) => (
                  <option key={trip.id} value={trip.id}>
                    {trip.name}{trip.destination ? ` — ${trip.destination}` : ''} ({trip.start_date} to {trip.end_date})
                  </option>
                ))}
              </select>
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {/* Date */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Date</label>
              <input
                type="date"
                value={formData.date}
                onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-green-500 focus:border-transparent"
              />
            </div>

            {/* Payee */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Payee</label>
              <input
                type="text"
                value={formData.payee}
                onChange={(e) => setFormData({ ...formData, payee: e.target.value })}
                placeholder="e.g., Uber, Marriott, Amazon"
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-green-500 focus:border-transparent"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {/* Amount */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Amount ($)</label>
              <input
                type="number"
                step="0.01"
                min="0.01"
                value={formData.amount}
                onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                onBlur={handleAmountBlur}
                placeholder="0.00"
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-green-500 focus:border-transparent"
              />
            </div>

            {/* Payment Method */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Payment Method</label>
              <select
                value={formData.payment_method}
                onChange={(e) => setFormData({ ...formData, payment_method: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-green-500 focus:border-transparent"
              >
                {Object.entries(PAYMENT_METHOD_LABELS).map(([value, label]) => (
                  <option key={value} value={value}>{label}</option>
                ))}
              </select>
            </div>

            {/* Reimbursable */}
            <div className="flex items-end pb-2">
              <label className="flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.is_reimbursable}
                  onChange={(e) => setFormData({ ...formData, is_reimbursable: e.target.checked })}
                  className="rounded border-gray-300 text-green-600 focus:ring-green-500 h-4 w-4"
                />
                <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">Reimbursable</span>
              </label>
            </div>
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Description</label>
            <input
              type="text"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Brief description of the expense"
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-green-500 focus:border-transparent"
            />
          </div>

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Notes</label>
            <textarea
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              placeholder="Additional notes (optional)"
              rows={2}
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-green-500 focus:border-transparent"
            />
          </div>

          {/* Receipt Upload */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Receipt</label>
            <div className="flex items-center gap-3">
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                capture="environment"
                onChange={handleReceiptUpload}
                className="hidden"
              />
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                disabled={isUploadingReceipt}
                className="inline-flex items-center px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              >
                {isUploadingReceipt ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-green-600 mr-2" />
                ) : (
                  <CameraIcon className="h-4 w-4 mr-2" />
                )}
                {receiptPreview ? 'Replace Receipt' : 'Upload Receipt'}
              </button>

              {receiptPreview && (
                <button
                  type="button"
                  onClick={openReceiptViewerForForm}
                  className="inline-flex items-center px-3 py-2 border border-blue-300 dark:border-blue-600 rounded-lg text-sm text-blue-700 dark:text-blue-300 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors"
                >
                  <DocumentMagnifyingGlassIcon className="h-4 w-4 mr-2" />
                  View &amp; OCR
                </button>
              )}

              {receiptPreview && (
                <div className="relative">
                  <button
                    type="button"
                    onClick={openReceiptViewerForForm}
                    title="View receipt"
                    className="block"
                  >
                    <img
                      src={getReceiptUrl(receiptPreview)}
                      alt="Receipt"
                      className="h-16 w-16 object-cover rounded-lg border border-gray-200 dark:border-gray-600 hover:opacity-90 transition-opacity cursor-zoom-in"
                    />
                  </button>
                  <button
                    type="button"
                    onClick={() => { setReceiptPreview(null); setPendingReceipt(null); setPendingOcrData(null); }}
                    className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-0.5"
                    title="Remove receipt"
                  >
                    <XMarkIcon className="h-3 w-3" />
                  </button>
                </div>
              )}
            </div>
            {isOCRPrefilling && (
              <p className="mt-2 text-xs text-blue-600 dark:text-blue-400 flex items-center">
                <span className="animate-spin rounded-full h-3 w-3 border-b-2 border-blue-600 mr-2" />
                Reading receipt…
              </p>
            )}
          </div>

          {/* Policy Warnings */}
          {policyWarnings && policyWarnings.is_over_limit && (
            <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-3">
              <div className="flex items-start">
                <ExclamationTriangleIcon className="h-5 w-5 text-amber-500 mt-0.5 mr-2 flex-shrink-0" />
                <div>
                  <p className="text-sm font-medium text-amber-800 dark:text-amber-200">Policy Warning</p>
                  {policyWarnings.warnings.map((w, i) => (
                    <p key={i} className="text-xs text-amber-700 dark:text-amber-300 mt-1">{w.message}</p>
                  ))}
                </div>
              </div>
            </div>
          )}

          {formError && (
            <p className="text-sm text-red-600 dark:text-red-400">{formError}</p>
          )}

          <div className="flex justify-end space-x-3 pt-4">
            <Button variant="secondary" onClick={handleCloseModal} type="button">Cancel</Button>
            <Button type="submit" isLoading={isSubmitting}>Save</Button>
          </div>
        </form>
      </Modal>

      {/* Receipt Viewer Modal */}
      <Modal
        isOpen={!!viewingReceipt}
        onClose={() => setViewingReceipt(null)}
        title="Receipt"
        size="lg"
        nested
      >
        {viewingReceipt && (
          <div className="space-y-4">
            <div className="flex justify-center bg-gray-100 dark:bg-gray-900 rounded-lg p-2">
              <img
                src={getReceiptUrl(viewingReceipt.imageUrl)}
                alt="Receipt"
                className="max-h-[60vh] w-auto object-contain rounded"
              />
            </div>

            <div className="bg-gray-50 dark:bg-gray-700/40 rounded-lg p-4">
              <div className="flex items-center justify-between mb-2 gap-2">
                <div className="flex items-center gap-2">
                  <h4 className="text-sm font-semibold text-gray-900 dark:text-white">OCR Result</h4>
                  {viewingReceipt.ocr?.method && (
                    <span className="text-xs px-2 py-0.5 rounded-full bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300">
                      {viewingReceipt.ocr.method === 'vision' ? 'AI Vision' : 'Tesseract'}
                    </span>
                  )}
                </div>
                {(viewingReceipt.expenseId || viewingReceipt.imagePath) && (
                  <button
                    type="button"
                    onClick={handleViewerRunOCR}
                    disabled={isProcessingOCR}
                    className="inline-flex items-center px-3 py-1.5 border border-blue-300 dark:border-blue-600 rounded-lg text-xs font-medium text-blue-700 dark:text-blue-300 hover:bg-blue-50 dark:hover:bg-blue-900/20 disabled:opacity-50 transition-colors"
                  >
                    {isProcessingOCR ? (
                      <div className="animate-spin rounded-full h-3.5 w-3.5 border-b-2 border-blue-600 mr-2" />
                    ) : (
                      <DocumentMagnifyingGlassIcon className="h-3.5 w-3.5 mr-1.5" />
                    )}
                    {viewingReceipt.ocr ? 'Re-run OCR' : 'Run OCR'}
                  </button>
                )}
              </div>

              {viewerOCRError && (
                <p className="text-sm text-red-600 dark:text-red-400 mb-2">{viewerOCRError}</p>
              )}

              {viewingReceipt.ocr ? (
                <>
                  {renderOcrFields(viewingReceipt.ocr.extracted_fields)}
                  {viewingReceipt.ocr.raw_text && (
                    <details className="mt-3">
                      <summary className="text-xs text-gray-500 dark:text-gray-400 cursor-pointer">Raw OCR text</summary>
                      <pre className="mt-2 text-xs whitespace-pre-wrap text-gray-600 dark:text-gray-300 max-h-40 overflow-y-auto">{viewingReceipt.ocr.raw_text}</pre>
                    </details>
                  )}
                </>
              ) : (
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {isProcessingOCR ? 'Reading receipt…' : 'No OCR data yet — click “Run OCR” to read this receipt.'}
                </p>
              )}
            </div>

            {/* Itemized breakdown / split — e.g. hotel: lodging vs meals, by day */}
            {viewingReceipt.ocr && (viewerBreakdown().length > 1 || viewingReceipt.expenseId) && (
              <div className="bg-gray-50 dark:bg-gray-700/40 rounded-lg p-4">
                <div className="flex items-center justify-between mb-2 gap-2">
                  <h4 className="text-sm font-semibold text-gray-900 dark:text-white">
                    {splitConfirm ? 'Split into separate expenses' : 'Category breakdown'}
                  </h4>
                  {viewingReceipt.expenseId && !splitConfirm && (
                    <button
                      type="button"
                      onClick={beginSplit}
                      className="inline-flex items-center px-3 py-1.5 border border-blue-300 dark:border-blue-600 rounded-lg text-xs font-medium text-blue-700 dark:text-blue-300 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors"
                    >
                      <ScissorsIcon className="h-3.5 w-3.5 mr-1.5" />
                      Split by category…
                    </button>
                  )}
                </div>

                {!splitConfirm ? (
                  viewerBreakdown().length <= 1 ? (
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      Logged as a single expense. Use <span className="font-medium">Split by category…</span> to break out meals from lodging (by day).
                    </p>
                  ) : (
                  <>
                    <div className="divide-y divide-gray-200 dark:divide-gray-600/50">
                      {viewerBreakdown().map((row, i) => {
                        const cat = activeCategories.find(c => c.name.toLowerCase() === String(row.category || '').toLowerCase());
                        return (
                          <div key={i} className="flex items-center justify-between gap-3 py-2 text-sm">
                            <div className="min-w-0 flex items-center gap-2 flex-wrap">
                              {row.date && (
                                <span className="text-xs text-gray-400 dark:text-gray-500 tabular-nums whitespace-nowrap">
                                  {new Date(row.date + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                                </span>
                              )}
                              <span
                                className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium"
                                style={{ backgroundColor: `${cat?.color || '#6B7280'}20`, color: cat?.color || '#6B7280' }}
                              >
                                {row.category || 'Uncategorized'}
                              </span>
                              {row.description && (
                                <span className="text-gray-500 dark:text-gray-400 truncate">{row.description}</span>
                              )}
                            </div>
                            <span className="font-medium text-gray-900 dark:text-white whitespace-nowrap">${row.amount.toFixed(2)}</span>
                          </div>
                        );
                      })}
                    </div>
                    <div className="flex items-center justify-between border-t border-gray-200 dark:border-gray-600 pt-2 mt-1 text-sm">
                      <span className="font-semibold text-gray-700 dark:text-gray-300">Total</span>
                      <span className="font-bold text-gray-900 dark:text-white">
                        ${viewerBreakdown().reduce((s, r) => s + r.amount, 0).toFixed(2)}
                      </span>
                    </div>
                  </>
                  )
                ) : (
                  <>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
                      Adjust the lines so lodging and meals are separated (each line becomes its own expense, dated per row). AI's guess is pre-filled — edit anything.
                    </p>
                    <div className="space-y-2">
                      {splitRows.map((row, i) => (
                        <div key={i} className="rounded-lg border border-gray-200 dark:border-gray-600 p-2 space-y-2 bg-white dark:bg-gray-800">
                          <div className="flex items-center gap-2">
                            <select
                              value={row.category_id}
                              onChange={(e) => updateSplitRow(i, { category_id: e.target.value })}
                              className="flex-1 min-w-0 px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-1 focus:ring-green-500"
                            >
                              <option value="">Category…</option>
                              {activeCategories.map(c => (
                                <option key={c.id} value={c.id}>{c.icon} {c.name}</option>
                              ))}
                            </select>
                            <div className="relative flex-shrink-0">
                              <span className="absolute left-2 top-1/2 -translate-y-1/2 text-sm text-gray-400">$</span>
                              <input
                                type="number" step="0.01" min="0" inputMode="decimal"
                                value={row.amount}
                                onChange={(e) => updateSplitRow(i, { amount: e.target.value })}
                                className="w-24 pl-5 pr-2 py-1.5 text-sm text-right border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-1 focus:ring-green-500"
                              />
                            </div>
                            <button
                              type="button"
                              onClick={() => removeSplitRow(i)}
                              className="p-1.5 text-gray-400 hover:text-red-500 rounded flex-shrink-0"
                              title="Remove line"
                            >
                              <XMarkIcon className="h-4 w-4" />
                            </button>
                          </div>
                          <div className="flex items-center gap-2">
                            <input
                              type="date"
                              value={row.date}
                              onChange={(e) => updateSplitRow(i, { date: e.target.value })}
                              className="flex-shrink-0 px-2 py-1.5 text-xs border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-1 focus:ring-green-500"
                            />
                            <input
                              type="text"
                              value={row.description}
                              onChange={(e) => updateSplitRow(i, { description: e.target.value })}
                              placeholder="Note (e.g. AC Lounge breakfast)"
                              className="flex-1 min-w-0 px-2 py-1.5 text-xs border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-1 focus:ring-green-500"
                            />
                          </div>
                        </div>
                      ))}
                    </div>

                    <button
                      type="button"
                      onClick={addSplitRow}
                      className="mt-2 inline-flex items-center text-xs font-medium text-green-600 dark:text-green-400 hover:underline"
                    >
                      <PlusIcon className="h-4 w-4 mr-1" />Add line
                    </button>

                    {(() => {
                      const src = viewerSourceExpense();
                      const sum = splitRowsTotal();
                      const target = src?.amount ?? sum;
                      const diff = Math.round((target - sum) * 100) / 100;
                      return (
                        <div className="mt-2 flex items-center justify-between text-sm border-t border-gray-200 dark:border-gray-600 pt-2">
                          <span className="text-gray-500 dark:text-gray-400">
                            Lines total <span className="font-semibold text-gray-900 dark:text-white">${sum.toFixed(2)}</span>
                            <span className="text-gray-400"> / receipt ${target.toFixed(2)}</span>
                          </span>
                          {Math.abs(diff) >= 0.01 && (
                            <span className="text-amber-600 dark:text-amber-400 text-xs">off by ${Math.abs(diff).toFixed(2)}</span>
                          )}
                        </div>
                      );
                    })()}

                    <div className="flex justify-end gap-2 mt-3">
                      <Button variant="secondary" type="button" onClick={() => { setSplitConfirm(false); setSplitRows([]); }}>Cancel</Button>
                      <Button type="button" onClick={handleSplitReceipt} isLoading={isSplitting}
                        disabled={splitRows.filter(r => (parseFloat(r.amount) || 0) > 0).length < 2}>
                        Split into {splitRows.filter(r => (parseFloat(r.amount) || 0) > 0).length}
                      </Button>
                    </div>
                  </>
                )}
              </div>
            )}

            <div className="flex justify-end pt-1">
              <a
                href={getReceiptUrl(viewingReceipt.imageUrl)}
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm text-green-600 dark:text-green-400 hover:underline"
              >
                Open full image ↗
              </a>
            </div>
          </div>
        )}
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        title="Delete Expense"
      >
        <div className="space-y-4">
          <p className="text-gray-600 dark:text-gray-400">Are you sure you want to delete this expense?</p>
          {deletingExpense && (
            <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-3">
              <p className="text-sm text-gray-900 dark:text-white font-medium">{deletingExpense.payee}</p>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">${deletingExpense.amount.toFixed(2)}</p>
            </div>
          )}
          <div className="flex justify-end space-x-3 pt-4">
            <Button variant="secondary" onClick={() => setIsDeleteModalOpen(false)}>Cancel</Button>
            <Button variant="danger" onClick={handleConfirmDelete} isLoading={isSubmitting}>Delete</Button>
          </div>
        </div>
      </Modal>

      {/* Click outside handler for calendar */}
      {showCalendar && (
        <div className="fixed inset-0 z-40" onClick={() => setShowCalendar(false)} />
      )}

      {/* Trip Create/Edit Modal */}
      <Modal
        isOpen={isTripModalOpen}
        onClose={() => setIsTripModalOpen(false)}
        title={editingTrip ? 'Edit Trip' : 'New Business Trip'}
        size="lg"
      >
        <form onSubmit={handleTripSubmit} className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="sm:col-span-2">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Trip Name</label>
              <input
                type="text"
                value={tripFormData.name}
                onChange={(e) => setTripFormData({ ...tripFormData, name: e.target.value })}
                placeholder="e.g., Chicago Client Visit, Q2 Trade Show"
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-green-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Project</label>
              <select
                value={tripFormData.project_id}
                onChange={async (e) => {
                  const pid = e.target.value;
                  setTripFormData({ ...tripFormData, project_id: pid, subproject_id: '' });
                  if (pid) {
                    const sps = await subprojectsAPI.list(pid);
                    setTripSubprojects(sps.filter(s => s.is_active));
                  } else {
                    setTripSubprojects([]);
                  }
                }}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-green-500 focus:border-transparent"
              >
                <option value="">Select a project...</option>
                {groupedProjects.map((group) => (
                  <optgroup key={group.client.id} label={group.client.name}>
                    {group.projects.map((project) => (
                      <option key={project.id} value={project.id}>{project.name}</option>
                    ))}
                  </optgroup>
                ))}
              </select>
            </div>
            {tripSubprojects.length > 0 && (
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Subproject <span className="text-gray-400 font-normal">(optional)</span>
                </label>
                <select
                  value={tripFormData.subproject_id}
                  onChange={(e) => setTripFormData({ ...tripFormData, subproject_id: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-green-500 focus:border-transparent"
                >
                  <option value="">No subproject</option>
                  {tripSubprojects.map((sp) => (
                    <option key={sp.id} value={sp.id}>{sp.name}</option>
                  ))}
                </select>
              </div>
            )}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Destination</label>
              <input
                type="text"
                value={tripFormData.destination}
                onChange={(e) => setTripFormData({ ...tripFormData, destination: e.target.value })}
                placeholder="e.g., Chicago, IL"
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-green-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Start Date</label>
              <input
                type="date"
                value={tripFormData.start_date}
                onChange={(e) => setTripFormData({ ...tripFormData, start_date: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-green-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">End Date</label>
              <input
                type="date"
                value={tripFormData.end_date}
                onChange={(e) => setTripFormData({ ...tripFormData, end_date: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-green-500 focus:border-transparent"
              />
            </div>
            {editingTrip && (
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Status</label>
                <select
                  value={tripFormData.status}
                  onChange={(e) => setTripFormData({ ...tripFormData, status: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-green-500 focus:border-transparent"
                >
                  <option value="active">Active</option>
                  <option value="completed">Completed</option>
                  <option value="submitted">Submitted</option>
                </select>
              </div>
            )}
            <div className="sm:col-span-2">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Business Purpose</label>
              <textarea
                value={tripFormData.purpose}
                onChange={(e) => setTripFormData({ ...tripFormData, purpose: e.target.value })}
                placeholder="Describe the business purpose of this trip..."
                rows={3}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-green-500 focus:border-transparent"
              />
            </div>
          </div>
          {tripFormError && <p className="text-sm text-red-600 dark:text-red-400">{tripFormError}</p>}
          <div className="flex justify-end space-x-3 pt-2">
            <Button variant="secondary" onClick={() => setIsTripModalOpen(false)} type="button">Cancel</Button>
            <Button type="submit" isLoading={isTripSubmitting}>Save Trip</Button>
          </div>
        </form>
      </Modal>

      {/* Delete Trip Confirmation Modal */}
      <Modal
        isOpen={isDeletingTrip}
        onClose={() => { setIsDeletingTrip(false); setTripToDelete(null); }}
        title="Delete Trip"
      >
        <div className="space-y-4">
          <p className="text-gray-600 dark:text-gray-400">
            Are you sure you want to delete this trip? Associated expenses will not be deleted — they will simply be unlinked from the trip.
          </p>
          {tripToDelete && (
            <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-3">
              <p className="text-sm font-medium text-gray-900 dark:text-white">{tripToDelete.name}</p>
              {tripToDelete.destination && <p className="text-sm text-gray-500 dark:text-gray-400">{tripToDelete.destination}</p>}
              <p className="text-sm text-gray-500 dark:text-gray-400">{tripToDelete.expense_count} expense{tripToDelete.expense_count !== 1 ? 's' : ''} · ${tripToDelete.total_amount.toFixed(2)}</p>
            </div>
          )}
          <div className="flex justify-end space-x-3 pt-2">
            <Button variant="secondary" onClick={() => { setIsDeletingTrip(false); setTripToDelete(null); }}>Cancel</Button>
            <Button variant="danger" onClick={handleConfirmDeleteTrip} isLoading={isTripSubmitting}>Delete Trip</Button>
          </div>
        </div>
      </Modal>

      {/* Hidden capture input — one page per invocation (native camera) */}
      <input
        ref={captureInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        onChange={handleCaptureInput}
        className="hidden"
      />

      {/* Multi-page Receipt Capture Modal */}
      <Modal
        isOpen={isCaptureOpen}
        onClose={cancelCapture}
        title="Snap Receipt"
        size="lg"
      >
        <div className="space-y-4">
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Snap each page of your receipt. Add as many pages as you need — they'll be combined into one expense and read together.
          </p>

          {captureError && (
            <p className="text-sm text-red-600 dark:text-red-400">{captureError}</p>
          )}

          {/* Captured page thumbnails */}
          {capturePages.length > 0 ? (
            <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
              {capturePages.map((page, idx) => (
                <div key={idx} className="relative">
                  <img
                    src={getReceiptUrl(page.thumbnail_url)}
                    alt={`Page ${idx + 1}`}
                    className="w-full h-28 object-cover rounded-lg border border-gray-200 dark:border-gray-600"
                  />
                  <span className="absolute bottom-1 left-1 text-[10px] font-medium px-1.5 py-0.5 rounded bg-black/60 text-white">
                    Page {idx + 1}
                  </span>
                  <button
                    type="button"
                    onClick={() => removeCapturePage(idx)}
                    className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-0.5"
                    title="Remove page"
                  >
                    <XMarkIcon className="h-3.5 w-3.5" />
                  </button>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-gray-400 dark:text-gray-500">
              {isCaptureUploading ? (
                <div className="flex flex-col items-center gap-2">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600" />
                  <span className="text-sm">Adding page…</span>
                </div>
              ) : (
                <>
                  <CameraIcon className="h-10 w-10 mx-auto mb-2 opacity-40" />
                  <p className="text-sm">No pages yet.</p>
                </>
              )}
            </div>
          )}

          {/* Add-page button */}
          <button
            type="button"
            onClick={addCapturePage}
            disabled={isCaptureUploading || isCombining}
            className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg text-sm font-medium text-gray-700 dark:text-gray-300 hover:border-green-400 hover:text-green-600 dark:hover:text-green-400 disabled:opacity-50 transition-colors"
          >
            {isCaptureUploading ? (
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-green-600" />
            ) : (
              <CameraIcon className="h-5 w-5" />
            )}
            {capturePages.length === 0 ? 'Snap first page' : 'Add another page'}
          </button>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-2">
            <Button variant="secondary" type="button" onClick={cancelCapture}>Cancel</Button>
            <Button
              type="button"
              onClick={finishCapture}
              isLoading={isCombining}
              disabled={capturePages.length === 0 || isCaptureUploading}
            >
              {capturePages.length > 1 ? `Done — ${capturePages.length} pages` : 'Done'}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Report generation progress — stays until the download is ready */}
      {exportBusy && (
        <div className="fixed bottom-20 sm:bottom-6 left-1/2 -translate-x-1/2 z-50 bg-gray-900 text-white px-4 py-3 rounded-lg shadow-lg flex items-center gap-3 max-w-[92vw]">
          <svg className="animate-spin h-5 w-5 flex-shrink-0" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
          </svg>
          <span className="text-sm font-medium">Generating {exportBusy} report — this can take a moment for trips with many receipts…</span>
        </div>
      )}

      {/* Snap auto-save toast */}
      {snapMessage && (
        <div className="fixed bottom-20 sm:bottom-6 left-1/2 -translate-x-1/2 z-50 bg-green-600 text-white px-4 py-3 rounded-lg shadow-lg flex items-center gap-2 max-w-[92vw]">
          <svg className="h-5 w-5 flex-shrink-0" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <span className="text-sm font-medium">{snapMessage}</span>
        </div>
      )}

      {/* Mobile FABs - Camera and Add Expense */}
      {activeTab === 'expenses' && activeProjects.length > 0 && (
        <>
          <button
            onClick={() => startReceiptCapture()}
            className="fixed bottom-6 right-24 lg:hidden z-30 w-14 h-14 bg-blue-600 hover:bg-blue-700 text-white rounded-full shadow-lg flex items-center justify-center transition-colors"
            title="Snap Receipt"
          >
            <CameraIcon className="h-7 w-7" />
          </button>
          <button
            onClick={() => handleOpenModal()}
            className="fixed bottom-6 right-6 lg:hidden z-30 w-14 h-14 bg-green-600 hover:bg-green-700 text-white rounded-full shadow-lg flex items-center justify-center transition-colors"
            title="Add Expense"
          >
            <PlusIcon className="h-7 w-7" />
          </button>
        </>
      )}
    </div>
  );
};
